import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import LogoFull from './LogoFull';
import Button from './Button';
import TenantSwitcher from './TenantSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { getRequests, subscribe } from '../../services/requestStore';
import { useTenant } from '../../context/TenantContext';

/**
 * Header, role-aware navigation + notification surface.
 *
 * Reads the logged-in user from localStorage (set by LoginForm). Different
 * roles see different navigation:
 *   - admin / manager → full HR admin nav + Tenant switcher + More menu
 *   - employee        → Meine Vergütung only (their personal portal)
 *   - auditor         → Compliance Reports only (auditor portal)
 *
 * Notifications are dynamic and pull from the shared requestStore so admins
 * see pending employee requests and employees see when their requests are
 * completed.
 */
const Header = () => {
  const { t, i18n } = useTranslation();
  const { branding, tenantLocked } = useTenant();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // ─── Current user (from login flow) ────────────────────────────────────────
  const readUser = () => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const [user, setUser] = useState(readUser);
  useEffect(() => {
    const handler = () => setUser(readUser());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Profile photo, reads from localStorage per user, updates on photo-changed events
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    const readPhoto = () => {
      if (!user?.email) return setPhoto(null);
      try {
        const stored = localStorage.getItem(`clearequal.profile.photo.${user.email}`);
        setPhoto(stored || null);
      } catch {
        setPhoto(null);
      }
    };
    readPhoto();
    window.addEventListener('clearequal:photo-changed', readPhoto);
    window.addEventListener('storage', readPhoto);
    return () => {
      window.removeEventListener('clearequal:photo-changed', readPhoto);
      window.removeEventListener('storage', readPhoto);
    };
  }, [user?.email]);
  const role = user?.role || 'admin'; // default admin so existing flows keep working
  const isEmployee = role === 'employee';
  const isAuditor = role === 'auditor';
  const isApprover = role === 'approver';
  const isAdmin = role === 'admin' || role === 'manager';

  // ─── Navigation per role ───────────────────────────────────────────────────
  const adminNav = [
    { labelKey: 'nav.dashboard',   path: '/analytics-dashboard', icon: 'BarChart3' },
    { labelKey: 'nav.assessments', path: '/joint-assessments',   icon: 'FileCheck' },
    { labelKey: 'nav.reports',     path: '/compliance-reports',  icon: 'FileText' },
    { labelKey: 'nav.payBands',    path: '/pay-band-management', icon: 'Layers' },
    { labelKey: 'nav.myPay',       path: '/employee-portal',     icon: 'User' }
  ];
  const employeeNav = [
    { labelKey: 'nav.myPay',       path: '/employee-portal',     icon: 'User' }
  ];
  const auditorNav = [
    { labelKey: 'nav.reports',     path: '/compliance-reports',  icon: 'FileText' }
  ];
  const approverNav = [
    { label: 'Approvals',          path: '/approvals',           icon: 'ShieldCheck' },
    { labelKey: 'nav.assessments', path: '/joint-assessments',   icon: 'FileCheck' },
    { labelKey: 'nav.reports',     path: '/compliance-reports',  icon: 'FileText' },
  ];

  const navigationItems = isEmployee
    ? employeeNav
    : isAuditor
    ? auditorNav
    : isApprover
    ? approverNav
    : adminNav;
  const moreItems = isAdmin
    ? [
        { labelKey: 'nav.dataIntegration', path: '/data-integration', icon: 'Database' },
        { label: 'Approvals',              path: '/approvals',         icon: 'ShieldCheck' },
        { label: 'Admin Console',          path: '/admin/console',     icon: 'Settings' },
      ]
    : [];

  // ─── Live notifications from request store ─────────────────────────────────
  const [allRequests, setAllRequests] = useState(() => getRequests());
  useEffect(() => {
    setAllRequests(getRequests());
    return subscribe(() => setAllRequests(getRequests()));
  }, [i18n.language]);

  // Build notification list per role
  const notifications = (() => {
    if (isEmployee) {
      // Employee sees their completed requests as notifications
      return allRequests
        .filter((r) => r.status === 'completed')
        .slice(0, 5)
        .map((r) => ({
          id: r.id,
          title: t('notifications.requestCompletedTitle'),
          message: t('notifications.requestCompletedBody', { title: r.title }),
          time: r.completedDate || '',
          type: 'success',
          target: '/employee-portal',
          scrollTo: `request-${r.id}`,
          // Tab hint, the request lives in the 'requests' tab on the employee portal
          tabHint: 'requests',
        }));
    }
    if (isAdmin) {
      // Admin sees pending employee requests
      return allRequests
        .filter((r) => r.status === 'pending')
        .slice(0, 5)
        .map((r) => ({
          id: r.id,
          title: t('notifications.newRequestTitle'),
          message: t('notifications.newRequestBody', { employee: r.employeeName || 'Employee', title: r.title }),
          time: r.requestDate || '',
          type: 'warning',
          target: '/analytics-dashboard',
          scrollTo: `pending-request-${r.id}`,
        }));
    }
    return [];
  })();

  const isActivePath = (path) => location?.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  /**
   * handleNotificationClick, navigate to the notification's target page if
   * we're not already there, then scroll to the relevant element and briefly
   * highlight it.
   *
   * The tricky part: when we switch tabs (e.g. from Überblick → Auskunfts-
   * ersuchen), the target element doesn't exist in the DOM yet. We retry the
   * lookup every 60ms for up to ~2 seconds until React renders it.
   */
  const handleNotificationClick = (notification) => {
    if (!notification) return;
    const { target, scrollTo, tabHint } = notification;
    const onSamePage = target ? location.pathname === target : true;

    // Tell whichever page is mounted to switch to the right tab
    if (tabHint) {
      window.dispatchEvent(new CustomEvent('clearequal.switchTab', { detail: { tab: tabHint } }));
    }

    // Poll the DOM for the target element. Gives up after ~2 seconds.
    const attemptScroll = (attemptsLeft = 30) => {
      if (!scrollTo) return;
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('clearequal-notification-pulse');
        setTimeout(() => el.classList.remove('clearequal-notification-pulse'), 2500);
        return;
      }
      if (attemptsLeft > 0) {
        setTimeout(() => attemptScroll(attemptsLeft - 1), 60);
      }
    };

    if (onSamePage) {
      // Even on same page, give the tab switch a moment to apply
      setTimeout(() => attemptScroll(), 50);
    } else if (target) {
      navigate(target);
      // After route change, start polling immediately, attemptScroll handles waiting
      setTimeout(() => attemptScroll(), 100);
    }
  };

  // Display name & email for the user-menu badge
  const displayName = user?.name || (isEmployee ? 'Pascal Schumachers' : t('auth.adminUser'));
  const displayEmail = user?.email || (isEmployee ? 'employee@clearequal.com' : 'admin@clearequal.com');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-soft">
      <div className="flex items-center justify-between h-16 px-3 sm:px-6">
        {/* Left zone: Logo + tenant switcher + navigation */}
        <div className="flex items-center gap-6">
        {/* Logo, tenant white-label aware */}
        <div className="flex items-center gap-3">
          <Link to={isEmployee ? '/employee-portal' : isAuditor ? '/compliance-reports' : '/analytics-dashboard'} className="flex items-center gap-2.5">
            {branding.logoUrl ? (
              // Customer's own logo when on their tenant subdomain
              <img
                src={branding.logoUrl}
                alt={branding.displayName}
                className="h-8 object-contain"
                style={{ maxWidth: '180px' }}
              />
            ) : (
              <LogoFull variant="navy" size="sm" />
            )}
          </Link>
          {/* Tenant Switcher, anchored next to the brand mark, admin-only.
              Hidden when tenant is locked by subdomain (white-label mode). */}
          {isAdmin && !tenantLocked && (
            <>
              <div className="hidden lg:block h-6 w-px bg-border mx-1" />
              <div className="hidden lg:block">
                <TenantSwitcher />
              </div>
            </>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth whitespace-nowrap ${
                isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-accent hover:bg-accent/10'
              }`}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item.label || t(item.labelKey)}</span>
            </Link>
          ))}
          
          {/* More Menu, only shown when there are items in moreItems */}
          {moreItems.length > 0 && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                iconName="MoreHorizontal"
                iconSize={16}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-muted-foreground hover:text-accent whitespace-nowrap"
              >
                {t('nav.more')}
              </Button>
              {isMobileMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-elevated z-50">
                  {moreItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-popover-foreground hover:bg-accent/10 hover:text-accent transition-smooth"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item.label || t(item.labelKey)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center space-x-1.5 sm:space-x-3">
          {/* Mobile hamburger, only below lg breakpoint */}
          <button
            type="button"
            onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
            className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-smooth"
            aria-label={t('nav.toggleMenu')}
          >
            <Icon name={isMobileNavOpen ? "X" : "Menu"} size={22} />
          </button>
          {/* Language Switcher, sits before notifications */}
          <LanguageSwitcher />
          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative"
            >
              <Icon name="Bell" size={20} />
              {notifications?.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-warning text-warning-foreground text-xs rounded-full flex items-center justify-center">
                  {notifications?.length}
                </span>
              )}
            </Button>
            
            {isNotificationOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-md shadow-elevated z-50">
                <div className="p-4 border-b border-border">
                  <h3 className="font-medium text-popover-foreground">{t('notifications.title')}</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center">
                      <Icon name="BellOff" size={28} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium text-popover-foreground">{t('notifications.noNotifications')}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('notifications.noNotificationsBody')}</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <button
                        key={notification?.id}
                        type="button"
                        onClick={() => {
                          setIsNotificationOpen(false);
                          handleNotificationClick(notification);
                        }}
                        className="w-full text-left p-4 border-b border-border last:border-b-0 hover:bg-accent/10 transition-smooth"
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            notification?.type === 'warning' ? 'bg-warning' :
                            notification?.type === 'success' ? 'bg-success' : 'bg-primary'
                          }`} />
                          <div className="flex-1">
                            <p className="font-medium text-sm text-popover-foreground">{notification?.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">{notification?.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">{notification?.time}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="p-3 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      fullWidth
                      onClick={() => {
                        setIsNotificationOpen(false);
                        navigate(isEmployee ? '/employee-portal' : '/analytics-dashboard');
                      }}
                    >
                      {isEmployee ? t('notifications.goToMyPay') : t('notifications.goToRequests')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-1 sm:space-x-2 px-1.5 sm:px-3"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shrink-0 overflow-hidden">
                {photo ? (
                  <img src={photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Icon name="User" size={16} color="white" />
                )}
              </div>
              <span className="hidden 2xl:block text-sm font-medium">{displayName}</span>
              <Icon name="ChevronDown" size={16} className="hidden sm:inline" />
            </Button>
            
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-md shadow-elevated z-50">
                <div className="p-3 border-b border-border">
                  <p className="font-medium text-sm text-popover-foreground">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{displayEmail}</p>
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold ${
                    isEmployee ? 'bg-blue-100 text-blue-700'
                    : isAuditor ? 'bg-purple-100 text-purple-700'
                    : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {isEmployee ? t('auth.demoRoleEmployee') : isAuditor ? 'Auditor' : t('auth.demoRoleAdmin')}
                  </span>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      // Employees deep-link to the Mein Profil tab in the
                      // employee portal. Admins/auditors don't have a
                      // skill-profile, so we send them to Settings → Account.
                      if (isEmployee) {
                        navigate('/employee-portal?tab=profile');
                      } else {
                        navigate('/settings#account');
                      }
                    }}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-popover-foreground hover:bg-accent/10 hover:text-accent transition-smooth"
                  >
                    <Icon name="User" size={16} />
                    <span>{t('header.profile')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-popover-foreground hover:bg-accent/10 hover:text-accent transition-smooth"
                  >
                    <Icon name="Settings" size={16} />
                    <span>{t('header.settings')}</span>
                  </button>
                  <div className="border-t border-border my-1" />
                  <button 
                    onClick={handleLogout}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-smooth"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>{t('auth.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Icon name="Menu" size={20} />
          </Button>
        </div>
      </div>
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          {!tenantLocked && (
            <div className="px-4 py-3 border-b border-border">
              <TenantSwitcher />
            </div>
          )}
          <nav className="px-4 py-2 space-y-1">
            {[...navigationItems, ...moreItems]?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-accent hover:bg-accent/10'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item.label || t(item.labelKey)}</span>
              </Link>
            ))}
          </nav>
        </div>
      )}
      {/* Mobile nav dropdown, only when hamburger toggled */}
      {isMobileNavOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 bg-card border-b border-border shadow-elevated z-40">
          <nav className="flex flex-col p-2">
            {[...navigationItems, ...moreItems].map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileNavOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium transition-smooth ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item.label || t(item.labelKey)}</span>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;